import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Truck, LogOut, Phone, Mail, MapPin, Calendar, Clock,
  Package, AlertCircle, Car, User, Navigation, CheckCircle2,
  DollarSign, Play, Pause
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DriverDashboard = ({ currentDriver, onLogout }) => {
  const [jobs, setJobs] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(currentDriver.id);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('today');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState({});

  const getDriverAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('driver_token')}` }
  });

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchJobs();
    }, 30000);

    return () => clearInterval(interval);
  }, [selectedDriverId]);

  useEffect(() => {
    fetchJobs();
    if (currentDriver.role === 'supervisor') {
      fetchAllDrivers();
    }
  }, [selectedDriverId]);

  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/drivers/me/jobs`, getDriverAuthHeaders());
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllDrivers = async () => {
    try {
      // We need to use a tenant token to fetch all drivers
      // For now, supervisors will only see their own jobs unless we implement a different auth flow
      // This is a simplified version
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
    }
  };

  const updateJobStatus = async (jobId, newStatus) => {
    setIsUpdatingStatus(prev => ({ ...prev, [jobId]: true }));
    
    try {
      // Use the driver-specific status update endpoint
      await axios.patch(
        `${API}/drivers/jobs/${jobId}/status`,
        { status: newStatus },
        getDriverAuthHeaders()
      );

      // Update local state immediately
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === jobId ? { ...job, status: newStatus } : job
        )
      );

      toast.success(`Job status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('Failed to update job status:', error);
      toast.error('Failed to update job status');
    } finally {
      setIsUpdatingStatus(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const sendOnTheWayNotification = async (jobId) => {
    setIsUpdatingStatus(prev => ({ ...prev, [`${jobId}_onway`]: true }));
    
    try {
      // This would typically send SMS/email to customer
      // For now, we'll just show a success message (mocked)
      toast.success('Customer notified: Driver is on the way! 📱');
      
      // Update job status to in_progress as well
      await updateJobStatus(jobId, 'in_progress');
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setIsUpdatingStatus(prev => ({ ...prev, [`${jobId}_onway`]: false }));
    }
  };

  const filterJobsByTab = (jobs) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + 7);

    // Filter by selected driver if supervisor
    let filteredJobs = jobs;
    if (currentDriver.role === 'supervisor' && selectedDriverId !== 'all') {
      filteredJobs = jobs.filter(job => job.assigned_driver_id === selectedDriverId);
    }

    switch (activeTab) {
      case 'today':
        return filteredJobs.filter(job => {
          if (!job.scheduled_start) return false;
          const jobDate = new Date(job.scheduled_start);
          return jobDate >= today && jobDate < tomorrow;
        });
      case 'tomorrow':
        return filteredJobs.filter(job => {
          if (!job.scheduled_start) return false;
          const jobDate = new Date(job.scheduled_start);
          const tomorrowEnd = new Date(tomorrow);
          tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);
          return jobDate >= tomorrow && jobDate < tomorrowEnd;
        });
      case 'week':
        return filteredJobs.filter(job => {
          if (!job.scheduled_start) return false;
          const jobDate = new Date(job.scheduled_start);
          return jobDate >= today && jobDate <= weekEnd;
        });
      default:
        return filteredJobs;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled': return <Calendar className="w-4 h-4" />;
      case 'in_progress': return <Play className="w-4 h-4" />;
      case 'completed': return <CheckCircle2 className="w-4 h-4" />;
      case 'paid': return <DollarSign className="w-4 h-4" />;
      case 'paused': return <Pause className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const canUpdateToStatus = (currentStatus, targetStatus) => {
    const statusFlow = {
      'scheduled': ['in_progress', 'cancelled'],
      'in_progress': ['completed', 'paused', 'cancelled'],
      'paused': ['in_progress', 'cancelled'],
      'completed': ['paid'],
      'paid': [],
      'cancelled': []
    };
    
    return statusFlow[currentStatus]?.includes(targetStatus) || false;
  };

  const filteredJobs = filterJobsByTab(jobs);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-teal-600 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur">
                <Truck className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">
                  {currentDriver.first_name} {currentDriver.last_name}
                </h1>
                <p className="text-sm text-white/80">
                  {currentDriver.role === 'supervisor' ? 'Supervisor' : 'Driver'}
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={onLogout}
              className="gap-2 bg-white/10 border-white/30 text-white hover:bg-white/20"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Supervisor Driver Selector */}
        {currentDriver.role === 'supervisor' && (
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-gray-500" />
              <select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Drivers</option>
                <option value={currentDriver.id}>My Jobs</option>
                {/* Add other drivers when implemented */}
              </select>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[
            { id: 'today', label: 'Today' },
            { id: 'tomorrow', label: 'Tomorrow' },
            { id: 'week', label: 'This Week' }
          ].map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className="flex-shrink-0"
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Jobs Count */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {filteredJobs.length} {filteredJobs.length === 1 ? 'job' : 'jobs'} scheduled
          </p>
          <p className="text-xs text-gray-500">
            Auto-refreshes every 30 seconds
          </p>
        </div>

        {/* Jobs List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <Card className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-600 mb-1">No jobs scheduled</h3>
            <p className="text-sm text-gray-500">
              {activeTab === 'today' && "You don't have any jobs scheduled for today"}
              {activeTab === 'tomorrow' && "You don't have any jobs scheduled for tomorrow"}
              {activeTab === 'week' && "You don't have any jobs scheduled this week"}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <Card key={job.id} className="p-4 hover:shadow-lg transition-shadow">
                <div className="space-y-3">
                  {/* Job Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-lg">
                        {job.customer_name || 'Unknown Customer'}
                      </h3>
                      <p className="text-sm text-gray-500">Job #{job.job_number}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                      job.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                      job.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                      job.status === 'completed' ? 'bg-green-100 text-green-800' :
                      job.status === 'paid' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {getStatusIcon(job.status)}
                      {job.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>

                  {/* Schedule Info */}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      {formatDate(job.scheduled_start)}
                    </div>
                    {job.scheduled_start && (
                      <div className="flex items-center gap-1 text-gray-600">
                        <Clock className="w-4 h-4" />
                        {formatTime(job.scheduled_start)}
                      </div>
                    )}
                  </div>

                  {/* Address */}
                  {(job.scheduled_location || job.address_1 || job.postcode) && (
                    <div className="flex items-start gap-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        {job.scheduled_location && <div>{job.scheduled_location}</div>}
                        {job.address_1 && <div>{job.address_1}</div>}
                        {job.address_2 && <div>{job.address_2}</div>}
                        {job.city && <div>{job.city}</div>}
                        {job.postcode && <div className="font-semibold">{job.postcode}</div>}
                      </div>
                    </div>
                  )}

                  {/* Vehicles */}
                  {job.vehicles && job.vehicles.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {job.vehicles.map((vehicle, index) => (
                        <div 
                          key={index}
                          className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm"
                        >
                          <Car className="w-3 h-3 text-gray-500" />
                          <span className="font-medium">{vehicle.registration_number}</span>
                          <span className="text-gray-500">
                            {vehicle.make} {vehicle.model}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Service Items */}
                  {job.items && job.items.length > 0 && (
                    <div className="flex items-start gap-2 text-sm text-gray-600">
                      <Package className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        {job.items.map((item, index) => (
                          <div key={index}>
                            {item.description} (x{item.quantity})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {job.notes && (
                    <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                      <strong>Notes:</strong> {job.notes}
                    </div>
                  )}

                  {/* Special Instructions */}
                  {job.special_instructions && (
                    <div className="p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                      <strong>Instructions:</strong> {job.special_instructions}
                    </div>
                  )}

                  {/* Status Update Buttons */}
                  <div className="border-t pt-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Update Job Status:</h4>
                    <div className="flex flex-wrap gap-2">
                      {/* On My Way Button - Special button for scheduled jobs */}
                      {job.status === 'scheduled' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendOnTheWayNotification(job.id)}
                          disabled={isUpdatingStatus[`${job.id}_onway`]}
                          className="gap-2 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                        >
                          {isUpdatingStatus[`${job.id}_onway`] ? (
                            <div className="animate-spin w-3 h-3 border border-blue-600 border-t-transparent rounded-full"></div>
                          ) : (
                            <Navigation className="w-3 h-3" />
                          )}
                          On My Way
                        </Button>
                      )}

                      {/* Status Update Buttons */}
                      {canUpdateToStatus(job.status, 'in_progress') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateJobStatus(job.id, 'in_progress')}
                          disabled={isUpdatingStatus[job.id]}
                          className="gap-2"
                        >
                          {isUpdatingStatus[job.id] ? (
                            <div className="animate-spin w-3 h-3 border border-gray-600 border-t-transparent rounded-full"></div>
                          ) : (
                            <Play className="w-3 h-3" />
                          )}
                          Start Job
                        </Button>
                      )}

                      {canUpdateToStatus(job.status, 'completed') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateJobStatus(job.id, 'completed')}
                          disabled={isUpdatingStatus[job.id]}
                          className="gap-2 bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                        >
                          {isUpdatingStatus[job.id] ? (
                            <div className="animate-spin w-3 h-3 border border-green-600 border-t-transparent rounded-full"></div>
                          ) : (
                            <CheckCircle2 className="w-3 h-3" />
                          )}
                          Complete
                        </Button>
                      )}

                      {canUpdateToStatus(job.status, 'paid') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateJobStatus(job.id, 'paid')}
                          disabled={isUpdatingStatus[job.id]}
                          className="gap-2 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                        >
                          {isUpdatingStatus[job.id] ? (
                            <div className="animate-spin w-3 h-3 border border-purple-600 border-t-transparent rounded-full"></div>
                          ) : (
                            <DollarSign className="w-3 h-3" />
                          )}
                          Mark Paid
                        </Button>
                      )}

                      {canUpdateToStatus(job.status, 'paused') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateJobStatus(job.id, 'paused')}
                          disabled={isUpdatingStatus[job.id]}
                          className="gap-2"
                        >
                          {isUpdatingStatus[job.id] ? (
                            <div className="animate-spin w-3 h-3 border border-gray-600 border-t-transparent rounded-full"></div>
                          ) : (
                            <Pause className="w-3 h-3" />
                          )}
                          Pause
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Contact Buttons */}
                  <div className="flex gap-2 pt-2 border-t">
                    {job.customer_phone && (
                      <a 
                        href={`tel:${job.customer_phone}`}
                        className="flex-1"
                      >
                        <Button variant="outline" size="sm" className="w-full gap-2">
                          <Phone className="w-4 h-4" />
                          Call Customer
                        </Button>
                      </a>
                    )}
                    {job.contact_phone && (
                      <a 
                        href={`tel:${job.contact_phone}`}
                        className="flex-1"
                      >
                        <Button variant="outline" size="sm" className="w-full gap-2">
                          <Phone className="w-4 h-4" />
                          Call Contact
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverDashboard;
